import { CheckCircle, Clock, XCircle, Pause } from "lucide-react";

// ── Status metadata ─────────────────────────────────────────────
// Single source of truth for how an application status renders.
// Add a new status here and its matching CSS block (tone key) and
// every page that imports this stays in sync automatically.
export const STATUS_META = {
  approved: { label: "Approved", icon: CheckCircle, tone: "approved" },
  accepted: { label: "Approved", icon: CheckCircle, tone: "approved" },
  on_hold: { label: "On hold", icon: Pause, tone: "on_hold" },
  pending: { label: "Under review", icon: Clock, tone: "pending" },
  denied: { label: "Not selected", icon: XCircle, tone: "denied" },
};

export function normalizeStatus(raw) {
  const status = String(raw || "pending").toLowerCase();
  return STATUS_META[status] ? status : "pending";
}

// Builds /vendor/events/:slug consistently — event_key when present,
// falling back to the numeric id. Centralized so a future rename of
// this route only happens in one place.
export function getEventPath(event) {
  if (!event) return "/vendor/events";
  return `/vendor/events/${event.event_key || event.id}`;
}

export function formatEventDate(dateString) {
  if (!dateString) return "";
  return new Date(dateString).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function formatShortDate(dateString) {
  if (!dateString) return "";
  return new Date(dateString).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}

export function formatDeadline(dateString) {
  if (!dateString) return "";
  return new Date(dateString).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

// The four payment checkpoints that make up onboarding, in order.
// Used both to compute the next step and to drive the progress bar,
// so the two can never drift out of sync with each other.
const PAYMENT_STEPS = [
  "deposit_submitted",
  "deposit_confirmed",
  "balance_submitted",
  "balance_confirmed",
];

export function getPaymentProgress(app) {
  const completed = PAYMENT_STEPS.filter((key) => app[key]).length;
  return { completed, total: PAYMENT_STEPS.length };
}

/**
 * Determines the vendor's next actionable step from the payment flags
 * on a vendor_applications row. Informational only — not a hard gate
 * on anything else in the app.
 */
export function getNextStep(app) {
  // Mirrors the gate VendorEventWorkspace already enforces (it redirects to
  // VendorOnboarding whenever onboarding_completed is false) — this just
  // makes sure the homepage says the same thing before the vendor clicks
  // through, instead of prompting for a deposit they can't actually submit
  // yet.
  if (!app.onboarding_completed) {
    return {
      title: "Complete your onboarding",
      description: "Finish a few quick steps to set up your space.",
      actionLabel: "Continue onboarding",
      deadline: null,
    };
  }
  if (!app.deposit_submitted) {
    return {
      title: "Upload deposit receipt",
      description: "Submit proof of your deposit payment to hold your spot.",
      actionLabel: "Upload receipt",
      deadline: app.deposit_deadline || null,
    };
  }
  if (!app.deposit_confirmed) {
    return {
      title: "Deposit under review",
      description: "We're confirming your payment — this usually takes 1–2 business days.",
      actionLabel: "View details",
      deadline: null,
    };
  }
  if (!app.balance_submitted) {
    return {
      title: "Pay remaining balance",
      description: "Submit your final payment before the deadline to keep your spot.",
      actionLabel: "Pay balance",
      deadline: null,
    };
  }
  if (!app.balance_confirmed) {
    return {
      title: "Balance under review",
      description: "We're confirming your payment — this usually takes 1–2 business days.",
      actionLabel: "View details",
      deadline: null,
    };
  }
  return {
    title: "You're all set",
    description: "We'll send Pre-Event information before the event.",
    actionLabel: "View event",
    deadline: null,
  };
}